/**
 * Newsletter Popup Component
 *
 * Controls when and how the newsletter popup appears, intercepts the
 * customer-form submission via fetch so the discount-code success state
 * can be shown without a page reload, and handles the copy-to-clipboard
 * button for the discount code.
 */
class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.opened = false;
    this.timers = [];
    this.boundHandlers = {};
  }

  connectedCallback() {
    this.dialogComponent = this.querySelector('dialog-component');
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    this.formView = this.querySelector('[data-form-view]');
    this.successView = this.querySelector('[data-success-view]');
    this.errorView = this.querySelector('[data-error-view]');
    this.submitButton = this.querySelector('[data-submit-button]');
    this.copyButton = this.querySelector('[data-copy-button]');
    this.codeElement = this.querySelector('[data-code]');

    if (!this.dialogComponent || !this.dialog) return;

    this.config = {
      trigger: this.dataset.trigger || 'delay',
      delaySeconds: parseFloat(this.dataset.delaySeconds) || 0,
      scrollPercent: parseFloat(this.dataset.scrollPercent) || 50,
      autoDismissSeconds: parseFloat(this.dataset.autoDismissSeconds) || 0,
      frequency: this.dataset.frequency || 'session',
      frequencyDays: parseFloat(this.dataset.frequencyDays) || 7,
      pageTargeting: this.dataset.pageTargeting || 'all',
      template: this.dataset.template || '',
      designMode: this.dataset.designMode === 'true',
    };

    this.storageKey = `newsletter-popup:${this.id}`;

    // Listen on the popup itself via event delegation. This catches the
    // submit event even if the inner form is replaced or moved, and runs in
    // the capture phase so we always preventDefault before the browser
    // navigates.
    this.boundSubmit = this.handleSubmit.bind(this);
    this.addEventListener('submit', this.boundSubmit, true);

    this.setupCopyButton();

    // In the Shopify theme editor, always preview the popup so merchants can
    // see and edit it. Skip all gating.
    if (this.config.designMode) {
      this.scheduleOpen(0);
      return;
    }

    // If the form was just successfully posted (non-JS submission path),
    // open the popup and show the success view so the visitor sees the code.
    if (this.successView && !this.successView.hasAttribute('hidden')) {
      this.markSubscribed();
      this.scheduleOpen(0);
      return;
    }

    if (!this.shouldShowOnThisPage()) return;
    if (!this.shouldShowBasedOnFrequency()) return;
    if (this.hasSubscribed()) return;

    this.setupTrigger();
  }

  disconnectedCallback() {
    this.clearTimers();
    this.removeTriggerListeners();
  }

  shouldShowOnThisPage() {
    const t = this.config.pageTargeting;
    const template = this.config.template;
    if (t === 'all') return true;
    if (t === 'home') return template === 'index';
    if (t === 'not_cart') return template !== 'cart' && !template.startsWith('checkout');
    return true;
  }

  shouldShowBasedOnFrequency() {
    const f = this.config.frequency;
    if (f === 'always') return true;

    if (f === 'session') {
      try {
        return !sessionStorage.getItem(this.storageKey);
      } catch (e) {
        return true;
      }
    }

    if (f === 'once') {
      try {
        return !localStorage.getItem(this.storageKey);
      } catch (e) {
        return true;
      }
    }

    if (f === 'days') {
      try {
        const last = localStorage.getItem(this.storageKey);
        if (!last) return true;
        const lastDate = parseInt(last, 10);
        if (isNaN(lastDate)) return true;
        const elapsedDays = (Date.now() - lastDate) / (1000 * 60 * 60 * 24);
        return elapsedDays >= this.config.frequencyDays;
      } catch (e) {
        return true;
      }
    }

    return true;
  }

  hasSubscribed() {
    try {
      return localStorage.getItem(`${this.storageKey}:subscribed`) === '1';
    } catch (e) {
      return false;
    }
  }

  markSubscribed() {
    try {
      localStorage.setItem(`${this.storageKey}:subscribed`, '1');
    } catch (e) {
      // Storage unavailable — silently skip.
    }
  }

  setupTrigger() {
    const trigger = this.config.trigger;

    if (trigger === 'delay') {
      this.scheduleOpen(this.config.delaySeconds * 1000);
      return;
    }

    if (trigger === 'scroll') {
      let scrollRafId = 0;
      this.boundHandlers.scroll = () => {
        if (scrollRafId) return;
        scrollRafId = window.requestAnimationFrame(() => {
          scrollRafId = 0;
          const scrollTop = window.scrollY || document.documentElement.scrollTop;
          const docHeight = document.documentElement.scrollHeight - window.innerHeight;
          if (docHeight <= 0) return;
          const percent = (scrollTop / docHeight) * 100;
          if (percent >= this.config.scrollPercent) {
            this.openDialog();
            this.removeTriggerListeners();
          }
        });
      };
      window.addEventListener('scroll', this.boundHandlers.scroll, { passive: true });
      return;
    }

    if (trigger === 'exit') {
      this.boundHandlers.mouseout = (event) => {
        if (event.clientY <= 0 && !event.relatedTarget) {
          this.openDialog();
          this.removeTriggerListeners();
        }
      };
      document.addEventListener('mouseout', this.boundHandlers.mouseout);

      this.boundHandlers.fallback = () => this.openDialog();
      this.timers.push(setTimeout(this.boundHandlers.fallback, 60_000));
      return;
    }
  }

  removeTriggerListeners() {
    if (this.boundHandlers.scroll) {
      window.removeEventListener('scroll', this.boundHandlers.scroll);
    }
    if (this.boundHandlers.mouseout) {
      document.removeEventListener('mouseout', this.boundHandlers.mouseout);
    }
  }

  scheduleOpen(ms) {
    const id = setTimeout(() => this.openDialog(), ms);
    this.timers.push(id);
  }

  clearTimers() {
    for (const id of this.timers) clearTimeout(id);
    this.timers = [];
  }

  openDialog() {
    if (this.opened) return;
    this.opened = true;
    this.dialog.addEventListener(
      'close',
      () => {
        this.opened = false;
      },
      { once: true }
    );

    if (typeof this.dialogComponent.showDialog === 'function') {
      this.dialogComponent.showDialog();
    } else {
      this.dialog.showModal();
    }

    this.markShown();

    if (this.config.autoDismissSeconds > 0) {
      this.timers.push(setTimeout(() => this.closeDialog(), this.config.autoDismissSeconds * 1000));
    }
  }

  closeDialog() {
    if (typeof this.dialogComponent.closeDialog === 'function') {
      this.dialogComponent.closeDialog();
    } else if (this.dialog.open) {
      this.dialog.close();
    }
  }

  markShown() {
    if (this.config.designMode) return;
    const f = this.config.frequency;
    try {
      if (f === 'session') {
        sessionStorage.setItem(this.storageKey, '1');
      } else if (f === 'once' || f === 'days') {
        localStorage.setItem(this.storageKey, Date.now().toString());
      }
    } catch (e) {
      // Storage unavailable — silently skip.
    }
  }

  async handleSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== 'FORM' || !this.contains(form)) return;

    event.preventDefault();
    event.stopPropagation();

    const emailInput = form.querySelector('input[type="email"]');
    if (emailInput && !emailInput.checkValidity()) {
      this.showError(emailInput.validationMessage || 'Please enter a valid email address.');
      emailInput.focus();
      return;
    }

    this.setSubmitting(true);
    this.hideError();

    try {
      const formData = new FormData(form);
      await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'text/html' },
        redirect: 'follow',
      });

      // Any returned response means Shopify received the submission. Show
      // the discount code without trying to interpret the response body —
      // server-side errors (e.g. duplicate email) are rare and a returning
      // subscriber should still see the welcome code.
      this.showSuccess();
    } catch (err) {
      this.showError('Network error. Please try again.');
    } finally {
      this.setSubmitting(false);
    }
  }

  setSubmitting(busy) {
    if (!this.submitButton) return;
    this.submitButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    this.submitButton.disabled = busy;
  }

  showError(message) {
    if (!this.errorView) return;
    this.errorView.textContent = message;
    this.errorView.hidden = false;
  }

  hideError() {
    if (!this.errorView) return;
    this.errorView.textContent = '';
    this.errorView.hidden = true;
  }

  showSuccess() {
    if (this.formView) this.formView.hidden = true;
    if (this.successView) {
      this.successView.hidden = false;
      this.successView.focus?.();
    }
    this.markSubscribed();
  }

  setupCopyButton() {
    if (!this.copyButton || !this.codeElement) return;
    this.copyButton.addEventListener('click', this.handleCopy.bind(this));
  }

  async handleCopy() {
    const code = this.codeElement.textContent.trim();
    if (!code) return;

    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        copied = true;
      } else {
        copied = this.fallbackCopy(code);
      }
    } catch (e) {
      copied = this.fallbackCopy(code);
    }

    if (copied) {
      this.showCopiedState();
    }
  }

  fallbackCopy(text) {
    try {
      const range = document.createRange();
      range.selectNodeContents(this.codeElement);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      const ok = document.execCommand('copy');
      selection.removeAllRanges();
      return ok;
    } catch (e) {
      return false;
    }
  }

  showCopiedState() {
    const defaultLabel = this.copyButton.querySelector('[data-copy-default]');
    const successLabel = this.copyButton.querySelector('[data-copy-success]');
    if (!defaultLabel || !successLabel) return;

    defaultLabel.hidden = true;
    successLabel.hidden = false;
    this.copyButton.setAttribute('data-copied', 'true');

    clearTimeout(this.copiedResetTimer);
    this.copiedResetTimer = setTimeout(() => {
      defaultLabel.hidden = false;
      successLabel.hidden = true;
      this.copyButton.removeAttribute('data-copied');
    }, 2000);
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
